/**
 * CORE-1194 — чем именно оборвалась связь с хабом.
 *
 * Замер 02.09.2026 (прод, комната 202, 10:47–11:25) показал разрывы у КАЖДОГО
 * клиента комнаты раз в минуту-полторы: 36 циклов у участника, 39 у ведущего,
 * downtime 0,2–0,5 с. Канал при этом цел — переподключение мгновенное.
 *
 * Дальше замер упёрся в то, что событие `room_hub_lifecycle` не отвечает на
 * вопрос «кто положил трубку». В нём есть, КАК ДОЛГО жило соединение и сколько
 * заняло восстановление, но нет ни транспорта, ни ошибки, с которой SignalR
 * ушёл в переподключение, ни того, была ли вкладка на переднем плане. Три
 * версии — серверный таймаут молчащего клиента, задушенный транспорт и
 * подрезанные таймеры фоновой вкладки — этими полями различаются, а без них не
 * различаются никак. Это ровно урок CORE-756, записанный в CORE-897: событие,
 * поля которого не отвечают на вопрос, — это выброшенный ретест.
 *
 * Здесь только чистая часть: классификация и опознание. Замеры и подписки — у
 * тех, кто держит соединение (`useWatchPartyHub`, `chatApiChannel`).
 */

import { Platform } from 'react-native';

/** Каким транспортом соединение живёт на самом деле. */
export type HubTransport = 'websockets' | 'server-sent-events' | 'long-polling' | 'unknown';

/**
 * Чем оборвалось — насколько клиент вообще способен это различить.
 *
 * Значения выбраны так, чтобы каждое отвечало на свой вопрос, а не описывало
 * текст ошибки:
 *
 * - `server-timeout` — КЛИЕНТ решил, что сервер замолчал: за `serverTimeout`
 *   (60 с) не пришло ни одного кадра, включая серверные пинги. Значит проблема
 *   в направлении сервер→клиент.
 * - `probe-unanswered` — то же самое, но замечено раньше: сервер не ответил на
 *   `RequestSync` за 5 с, и клиент уронил соединение сам, не дожидаясь
 *   `serverTimeout` (CORE-1189, `hubDeafProbe`).
 * - `send-failed` — клиент попытался что-то отправить (обычно свой keep-alive
 *   раз в 15 с) и не смог: сокет уже был мёртв, а узнали мы об этом только на
 *   очередном тике. Отсюда и «ровные» аптаймы, кратные периоду пинга.
 * - `transport-closed` — транспорт закрылся сам и сказал об этом: код закрытия
 *   WebSocket, оборванный поток SSE, отменённый long-poll.
 * - `unauthorized` — протухший токен; это не сеть и чинится не здесь.
 * - `network` — запрос не ушёл вовсе (`Failed to fetch` и родня).
 */
export type HubDropCause =
  | 'server-timeout'
  | 'probe-unanswered'
  | 'send-failed'
  | 'transport-closed'
  | 'unauthorized'
  | 'network'
  | 'unknown';

/**
 * Какой транспорт выбрал SignalR.
 *
 * Спрашиваем у объекта соединения — публичного способа нет, а знать надо:
 * long-polling вместо WebSocket объяснил бы ровный таймаут сам по себе, и это
 * первая версия, которую нужно либо подтвердить, либо снять.
 *
 * Опознаём по СВОЙСТВАМ, а не по имени класса: имена классов минификатор
 * переписывает, имена свойств — нет. `_webSocket`, `_eventSource` и
 * `_pollAbort` — это поля соответствующих транспортов @microsoft/signalr.
 * Ошибиться здесь дёшево (`unknown` в замере), а сломать приложение нельзя:
 * всё чтение — необязательное и в try.
 */
export function describeHubTransport(connection: unknown): HubTransport {
  try {
    const inner = (connection as { connection?: unknown } | null)?.connection;
    const transport = (inner as { transport?: unknown } | null)?.transport as
      | Record<string, unknown>
      | undefined;
    if (!transport) return 'unknown';
    if ('_webSocket' in transport) return 'websockets';
    if ('_eventSource' in transport) return 'server-sent-events';
    if ('_pollAbort' in transport) return 'long-polling';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * К какому хосту шло соединение — `api.agapella.io` или РФ-эдж.
 *
 * Нужен отказавшему подъёму (`room_hub_start_failed`): на проде 09.10.2026
 * `negotiate` после возврата сети уходил на `api-ru`, и узнать это можно было
 * только из консоли тестировщика. Адрес целиком не кладём — хватает хоста.
 */
export function describeHubHost(connection: unknown): string | null {
  try {
    const url = (connection as { baseUrl?: unknown } | null)?.baseUrl;
    return typeof url === 'string' && url.length > 0 ? new URL(url).host : null;
  } catch {
    return null;
  }
}

/**
 * Каким транспортом жило соединение, которое только что оборвалось.
 *
 * Опрос по месту разрыва почти всегда пуст: уходя в переподключение, SignalR
 * сначала снимает транспорт с соединения и только потом зовёт
 * `onreconnecting`/`onclose`. На деве с 03.09 по 01.10 так ушли в `unknown`
 * почти все разрывы с известной причиной. Поэтому верим живому ответу, если он
 * есть, а иначе — запомненному на последнем подъёме соединения.
 */
export function lastKnownTransport(atDrop: HubTransport, lastLive: HubTransport): HubTransport {
  return atDrop !== 'unknown' ? atDrop : lastLive;
}

/**
 * Чем оборвалось — по тексту ошибки, с которой SignalR ушёл в переподключение.
 *
 * Текст в замер не кладём: он несёт адреса и коды и меняется от версии к
 * версии. Кладём разряд — его достаточно, чтобы разделить версии, и он
 * переживёт обновление библиотеки.
 */
export function classifyHubDrop(error: unknown): HubDropCause {
  const message = typeof error === 'string'
    ? error
    : (error as { message?: unknown } | null)?.message;
  if (typeof message !== 'string' || message.length === 0) return 'unknown';
  const text = message.toLowerCase();

  // «Server timeout elapsed without receiving a message from the server.»
  if (text.includes('server timeout')) return 'server-timeout';
  // `DEAF_HUB_ERROR_MESSAGE` — соединение уронил сторож глухого хаба.
  if (text.includes('sync probe unanswered')) return 'probe-unanswered';
  // «Cannot send data if the connection is not in the 'Connected' State.» —
  // это и есть неудачный keep-alive поверх уже мёртвого сокета.
  if (text.includes('cannot send data') || text.includes('websocket is not in the open state')) {
    return 'send-failed';
  }
  if (text.includes('401') || text.includes('unauthorized')) return 'unauthorized';
  if (text.includes('closed with status code')
    || text.includes('connection was closed')
    || (text.includes('transport') && text.includes('closed'))) {
    return 'transport-closed';
  }
  if (text.includes('failed to fetch')
    || text.includes('network')
    || text.includes('load failed')) {
    return 'network';
  }
  return 'unknown';
}

export interface VisibilityInput {
  /** `document.hidden` на момент разрыва. */
  hidden: boolean;
  /** Когда вкладка ушла в фон (мс epoch), или `null` — она на виду. */
  hiddenSinceMs: number | null;
  nowMs: number;
}

export interface VisibilityAtDrop {
  visibility: 'visible' | 'hidden';
  /** Сколько миллисекунд вкладка была в фоне К МОМЕНТУ разрыва. */
  hiddenMs: number;
}

/**
 * Была ли вкладка на переднем плане, когда соединение оборвалось, и как долго
 * до этого висела в фоне.
 *
 * Версия, ради которой это поле заводится: браузер подрезает таймеры фоновой
 * вкладки, а keep-alive SignalR — это setTimeout. Через пять минут в фоне
 * Chrome доводит подрезку до одного срабатывания в минуту, и пинг раз в 15 с
 * превращается в пинг раз в минуту — против серверного `ClientTimeoutInterval`
 * в 60 с. Если разрывы окажутся у скрытых вкладок, чинить надо не сеть.
 *
 * `hiddenMs` важнее самого флага: подрезка включается не сразу, и «в фоне 8
 * минут» и «в фоне 3 секунды» — это разные истории.
 */
export function visibilityAtDrop(input: VisibilityInput): VisibilityAtDrop {
  if (!input.hidden || input.hiddenSinceMs == null) {
    return { visibility: input.hidden ? 'hidden' : 'visible', hiddenMs: 0 };
  }
  return {
    visibility: 'hidden',
    hiddenMs: Math.max(0, input.nowMs - input.hiddenSinceMs),
  };
}

// ─── Отметка «вкладка в фоне» ────────────────────────────────────────────────
//
// Один наблюдатель на приложение, а не по одному на соединение: спрашивают его
// оба хаба, а жить он обязан дольше любой комнаты — иначе «в фоне восемь минут»
// обнулялось бы при каждом входе в комнату, то есть ровно там, где этот счёт и
// нужен. Слушатель не снимается по той же причине; он один и ничего не держит.
//
// Инвариант I-2 соблюдён: `document` живёт под проверкой `Platform.OS === 'web'`
// и наружу не протекает — на нативе счётчик просто всегда «на виду».

let hiddenSinceMs: number | null = null;
let watching = false;

function ensureVisibilityWatch(): void {
  if (watching || Platform.OS !== 'web' || typeof document === 'undefined') return;
  // Подписка не обязана удаться, и провалиться она не имеет права: телеметрия
  // не роняет ни комнату, ни чат. `document` бывает и неполным — серверный
  // рендер, jest-окружение `node` с частичной заглушкой; тогда просто
  // считаем, что вкладка на виду, а не падаем на пустом месте.
  watching = true;
  try {
    if (typeof document.addEventListener !== 'function') return;
    const sync = () => {
      hiddenSinceMs = document.hidden ? (hiddenSinceMs ?? Date.now()) : null;
    };
    sync();
    document.addEventListener('visibilitychange', sync);
  } catch {
    /* см. выше: нечем подписаться — значит, отметки просто не будет */
  }
}

/** Была ли вкладка на виду в момент разрыва — и как долго до него в фоне. */
export function readDropVisibility(nowMs: number = Date.now()): VisibilityAtDrop {
  ensureVisibilityWatch();
  return visibilityAtDrop({
    hidden: Platform.OS === 'web'
      && typeof document !== 'undefined'
      && document.hidden === true,
    hiddenSinceMs,
    nowMs,
  });
}

/** Тест-хелпер: модульное состояние между тестами само не сбрасывается. */
export function resetVisibilityWatchForTests(): void {
  hiddenSinceMs = null;
  watching = false;
}
